import { useWorkspaceText } from '../interface-copy';
import {
  Fragment,
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import connectionsCopy from '@/i18n/ns/connections';
import { formatCount } from '@/i18n/format';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { agentAccessApi } from './api';
import { CopyLine } from './copy-line';
import { agentErrorMessage, dayLabel, isLostResponse } from './format';
import { Glyph } from './glyphs';
import type { AgentAccessScopeInfo, CreatedAccessKey } from './types';

const DEFAULT_DAYS = 30;

/**
 * A catalog sentence whose `{name}` places hold elements (bold field names,
 * code), in the order the language puts them.
 */
function Phrase({ text, parts }: { text: string; parts: Record<string, ReactNode> }) {
  const nodes: ReactNode[] = [];
  let rest = text;
  while (rest) {
    const open = rest.indexOf('{');
    const close = open < 0 ? -1 : rest.indexOf('}', open);
    const name = close < 0 ? '' : rest.slice(open + 1, close);
    if (!Object.hasOwn(parts, name)) {
      nodes.push(rest);
      break;
    }
    nodes.push(rest.slice(0, open), parts[name]);
    rest = rest.slice(close + 1);
  }
  return (
    <>
      {nodes.map((node, index) => (
        <Fragment key={index}>{node}</Fragment>
      ))}
    </>
  );
}

/**
 * An access key for an external agent that can't sign in with OAuth (a CI
 * job, an API agent, a tool that only takes a token or header). The person
 * names it, picks what it may do and how long it lasts; the key is shown once.
 */
export function AccessKeyForm({
  optionalScopes,
  lifetimes,
  companyName,
  onCreated,
  onUncertain,
  onCancel,
}: {
  optionalScopes: AgentAccessScopeInfo[];
  lifetimes: number[];
  companyName: string;
  /** The key was created: the list refreshes, the key stays on screen once. */
  onCreated: (created: CreatedAccessKey) => void;
  /** The answer was lost: a key may exist that nobody saw, so re-read the list. */
  onUncertain: () => void;
  onCancel: () => void;
}) {
  const copy = useNamespaceCopy(connectionsCopy);
  const locale = useChatInterfaceLocale();
  const heading = useId();
  const nameId = useId();
  const lifetimeId = useId();
  const [label, setLabel] = useState('');
  const days = lifetimes.includes(DEFAULT_DAYS) ? DEFAULT_DAYS : (lifetimes[0] ?? DEFAULT_DAYS);
  const [lifetime, setLifetime] = useState(days);
  const [scopes, setScopes] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A company or person switch closes the form; a key that answers after that
  // is never shown under the next company or person.
  const alive = useRef(false);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const name = label.trim();
    if (!name || busy) return;
    setBusy(true);
    setError(null);
    try {
      const created = await agentAccessApi.createKey({
        label: name,
        scopes,
        lifetimeDays: lifetime,
      });
      if (alive.current) onCreated(created);
    } catch (failure) {
      if (!alive.current) return;
      const lost = isLostResponse(failure);
      if (lost) onUncertain();
      setError(
        lost
          ? copy.keyUnconfirmed
          : agentErrorMessage(failure, copy.keyNotCreated, copy.connectingAgentsOff, copy),
      );
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  return (
    <form className="ws-agent-key-form" aria-labelledby={heading} onSubmit={(e) => void submit(e)}>
      <h3 id={heading}>{copy.keyCreateAction}</h3>
      <p className="ws-agent-step-note">{formatCopy(copy.keyFormNote, { company: companyName })}</p>
      {/* Where a key may be pasted, said wherever a key is made or shown. */}
      <p className="ws-agent-step-note ws-agent-secret">
        <Glyph name="lock" size={13} />
        <span>{copy.keySecret}</span>
      </p>
      <div className="ws-agent-fields">
        <div className="ws-agent-field">
          <label htmlFor={nameId}>{copy.keyName}</label>
          {/* The person just asked for this form: the name is the next thing to do. */}
          <input
            id={nameId}
            type="text"
            value={label}
            maxLength={80}
            autoComplete="off"
            autoFocus
            placeholder={copy.keyNamePlaceholder}
            onChange={(event) => setLabel(event.target.value)}
          />
        </div>
        <div className="ws-agent-field">
          <label htmlFor={lifetimeId}>{copy.keyLifetime}</label>
          <select
            id={lifetimeId}
            value={lifetime}
            onChange={(event) => setLifetime(Number(event.target.value))}
          >
            {lifetimes.map((value) => (
              <option key={value} value={value}>
                {formatCount(copy.keyDays, value, locale)}
              </option>
            ))}
          </select>
        </div>
      </div>
      {optionalScopes.length > 0 && (
        <fieldset>
          <legend>{copy.keyAlsoAllow}</legend>
          {optionalScopes.map((scope) => (
            <label key={scope.id} className="ws-agent-check">
              <input
                type="checkbox"
                checked={scopes.includes(scope.id)}
                onChange={(event) =>
                  setScopes((current) =>
                    event.target.checked
                      ? [...current, scope.id]
                      : current.filter((id) => id !== scope.id),
                  )
                }
              />
              <span>
                <strong>{scope.label}</strong>
                <small>{scope.description}</small>
              </span>
            </label>
          ))}
        </fieldset>
      )}
      {error && (
        <p className="ws-agent-notice" data-tone="error" role="alert">
          {error}
        </p>
      )}
      <div className="ws-actions">
        {/* aria-disabled, not disabled: the pressed button keeps focus while
            it works, and submit() ignores it until there is a name. */}
        <button type="submit" className="ws-button primary" aria-disabled={busy || !label.trim()}>
          {busy ? copy.keyCreating : copy.keyCreate}
        </button>
        <button type="button" className="ws-text-button" disabled={busy} onClick={onCancel}>
          {copy.keyCancel}
        </button>
      </div>
    </form>
  );
}

/** The new key, shown once, with where it goes in an agent's settings. */
export function NewAccessKey({
  created,
  onDone,
}: {
  created: CreatedAccessKey;
  onDone: () => void;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const copy = useNamespaceCopy(connectionsCopy);
  const locale = useChatInterfaceLocale();
  const heading = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const app = created.client.clientName || copy.agentUnnamed;
  const expires = created.client.expiresAt
    ? dayLabel(created.client.expiresAt, copy, locale)
    : null;
  // It replaces the form whose button was pressed: focus lands on the key.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);
  return (
    <div className="ws-agent-key-new" role="group" aria-labelledby={heading}>
      <h3 id={heading} ref={headingRef} tabIndex={-1}>
        {formatCopy(copy.keyNewTitle, { app })}
      </h3>
      <p className="ws-agent-step-note" data-tone="attention">
        {copy.keyCopyNow}
      </p>
      <CopyLine value={created.key} label={formatCopy(copy.copyWhatKey, { app })} />
      <ul className="ws-agent-key-rules">
        <li>
          <Glyph name="lock" size={13} />
          <span>{copy.keySecret}</span>
        </li>
        <li>
          <Glyph name="key" size={13} />
          <span>
            <Phrase
              text={copy.keyTokenRule}
              parts={{
                token: <strong>{copy.keyTermToken}</strong>,
                apiKey: <strong>{copy.keyTermApiKey}</strong>,
              }}
            />
          </span>
        </li>
        <li>
          <Glyph name="key" size={13} />
          <span>
            <Phrase
              text={copy.keyHeaderRule}
              parts={{
                header: <strong>{copy.keyTermHeader}</strong>,
                name: <code>{uiText('Authorization')}</code>,
                value: <code>{'Bearer <key>'}</code>,
                key: '<key>',
              }}
            />
          </span>
        </li>
        <li>
          <Glyph name="clock" size={13} />
          <span>
            {expires ? formatCopy(copy.keyEndsOn, { date: expires }) : copy.keyEndsExpiry}
          </span>
        </li>
      </ul>
      <button type="button" className="ws-button" onClick={onDone}>
        {copy.keyDone}
      </button>
    </div>
  );
}
